import {
  WIDEN_DEFAULT_IMAGE_ID,
  WIDEN_DEFAULT_IMAGE_NAME,
  WIDEN_QUERY_PROPS,
  WIDEN_DEFAULT_IMAGE_TYPE,
  WIDEN_CDN_IMAGE_BASE_URL,
  WIDEN_IMAGE_FILE_EXTENSIONS,
  WIDEN_CDN_GIF_BASE_URL,
  WIDEN_CDN_VIDEO_BASE_URL,
  WIDEN_VIDEO_FILE_EXTENSION,
  WIDEN_GIF_FILE_EXTENSION,
  WIDEN_MAX_IMAGE_MEASUREMENT,
  APP_NAME,
} from './constants';
import type { WidenImageConfig } from '@hub/models/widen-image-config';
import type { WidenImagePropsKey } from '@hub/common/Widen/WidenImage';

export enum MediaElementType {
  IMAGE = 0,
  VIDEO = 1,
  DRAWING = 2,
  DIAGRAM = 3,
}

// sortorder defined to be able to sort mediaelements by type
export const mediaElementTypeSortOrder: MediaElementType[] = [
  MediaElementType.IMAGE,
  MediaElementType.DRAWING,
  MediaElementType.VIDEO,
];

/**
 * interface for Media
 */
export interface WidenMediaElement {
  widenId?: string;
  sort?: number;
  type: MediaElementType;
  filename: string;
  shortCode: string;
  title?: string;
  heading?: string;
  description?: string;
  underline?: string;
  priority?: boolean; // if true, element will be prioritized during loading by next
  technicalAttributes?: string[];
  marketingBadges?: string[];
}

export class WidenUtil {
  static isWidenImageUrl(url: string): boolean {
    // images are always displayed using base URL WIDEN_CDN_IMAGE_BASE_URL at constants.js
    // this function is necessary, however, because it is used at the product level and the akeneo / cms data might use either of these URLs
    return url.indexOf('embed.widencdn.net') !== -1 || url.indexOf('igus.widen.net') !== -1;
  }

  static extractWidenImageId(uri: string): string {
    if (!uri) {
      return WIDEN_DEFAULT_IMAGE_ID;
    }
    const splittedUri = uri.split('/');
    if (splittedUri?.length < 5) {
      return WIDEN_DEFAULT_IMAGE_ID;
    }

    // filename is located in a different place depending on the format of the Widen URL
    if (uri.indexOf('igus.widen.net') !== -1) {
      return splittedUri[4];
    }
    if (uri.indexOf('embed.widencdn.net') !== -1) {
      return splittedUri[5];
    }
    return WIDEN_DEFAULT_IMAGE_ID;
  }

  static extractWidenImageName(url: string): string {
    if (!url) {
      return WIDEN_DEFAULT_IMAGE_NAME;
    }

    const urlPart = url.split('/')[6];
    if (!urlPart) {
      return WIDEN_DEFAULT_IMAGE_NAME;
    }
    const urlSplitByQuestionMark = urlPart.split('?');
    return urlSplitByQuestionMark[0] ? urlSplitByQuestionMark[0] : WIDEN_DEFAULT_IMAGE_NAME;
  }

  static extractWidenVideo(uri: string): WidenMediaElement {
    if (!uri) {
      return {
        type: MediaElementType.VIDEO,
        shortCode: WIDEN_DEFAULT_IMAGE_ID,
        filename: WIDEN_DEFAULT_IMAGE_NAME,
      };
    }

    const splittedUri = uri.split('?')[0].split('/');

    if (splittedUri?.length < 6) {
      return {
        type: MediaElementType.VIDEO,
        shortCode: WIDEN_DEFAULT_IMAGE_ID,
        filename: WIDEN_DEFAULT_IMAGE_NAME,
      };
    }

    return {
      type: MediaElementType.VIDEO,
      shortCode: splittedUri[5],
      filename: splittedUri[6],
    };
  }

  /**
   * Returns the extension of a filename
   * @param filename name of the file whose extension should be returned
   * @returns a string containing the extension without the dot --> e.g. 'gif', 'png' etc.
   */
  static extractFileExtension(filename: string): string {
    const finalDotIndex = filename.lastIndexOf('.');
    return finalDotIndex !== -1 ? filename.substring(finalDotIndex + 1) : '';
  }

  /**
   * Replaces the extension of a filename
   * @param filename name of the file whose extension should be returned
   * @param newExtension extension which should replace the original one
   * @returns a filename with the new extension
   */
  static replaceFileExtension(filename: string, newExtension: string): string {
    const finalDotIndex = filename.lastIndexOf('.');
    return finalDotIndex !== -1 ? `${filename.substring(0, finalDotIndex)}.${newExtension}` : '';
  }

  /**
   * Creates the Widen query string from only the relevant properties.
   * @param props Props container passed to component
   */
  static createWidenQuery = (props: WidenImageConfig): string | undefined => {
    const queryString = Object.entries(props)
      .filter(
        ([propsKey, propsValue]) =>
          // filter out empty/undefined values and values which are not in the queryProps
          typeof propsValue !== 'undefined' && WIDEN_QUERY_PROPS.includes(propsKey as WidenImagePropsKey)
      )
      .map(([propsKey, propsValue]) => `${propsKey}=${propsValue as string}`)
      .join('&');
    return queryString ? `?${queryString}` : undefined;
  };

  /**
   * Generates the URL of an Element on Widen
   * @param widenImageConfig configuration that is supposed to be used for the URL generation
   * @returns a dynamic URL string depending on the type of file
   */
  static getWidenCDNUrl(widenImageConfig: WidenImageConfig): string {
    const fileExtension = this.extractFileExtension(widenImageConfig.name);
    let widenCDNUrl: string;

    const widenConfig = widenImageConfig;
    if (fileExtension === WIDEN_GIF_FILE_EXTENSION) {
      // animate should always be true for gifs
      widenConfig.animate = true;
      // if width and height on a gif on widen are set, it will not run the animation
      widenConfig.width = undefined;
      widenConfig.height = undefined;
    }
    const query = this.createWidenQuery(widenConfig);

    if (WIDEN_IMAGE_FILE_EXTENSIONS.some(extension => extension === fileExtension)) {
      // replace file extension of images with the default file extension
      const parsedFileName = this.replaceFileExtension(widenConfig.name, WIDEN_DEFAULT_IMAGE_TYPE);
      widenCDNUrl = `${WIDEN_CDN_IMAGE_BASE_URL}${widenConfig.widenKey}/${WIDEN_DEFAULT_IMAGE_TYPE}/${
        parsedFileName || WIDEN_DEFAULT_IMAGE_NAME
      }${query || ''}`;
    } else if (fileExtension === WIDEN_VIDEO_FILE_EXTENSION) {
      widenCDNUrl = `${WIDEN_CDN_VIDEO_BASE_URL}${widenConfig.widenKey}/${WIDEN_VIDEO_FILE_EXTENSION}/${
        widenConfig.name || WIDEN_DEFAULT_IMAGE_NAME
      }`;
    } else if (fileExtension === WIDEN_GIF_FILE_EXTENSION) {
      widenCDNUrl = `${WIDEN_CDN_GIF_BASE_URL}${widenConfig.widenKey}/${WIDEN_GIF_FILE_EXTENSION}/${
        widenConfig.name || WIDEN_DEFAULT_IMAGE_NAME
      }${query || ''}`;
    } else {
      widenCDNUrl = `${WIDEN_CDN_IMAGE_BASE_URL}${widenConfig.widenKey}/${
        fileExtension !== '' ? fileExtension : 'web'
      }/${widenConfig.name || WIDEN_DEFAULT_IMAGE_NAME}${query || ''}`;
    }

    return widenCDNUrl;
  }

  static generateWidenImageUrl(widenConfig: WidenImageConfig, placeholder?: string): string {
    return widenConfig.name && widenConfig.widenKey
      ? WidenUtil.getWidenCDNUrl(widenConfig)
      : (placeholder ?? `/_${APP_NAME}/images/default.svg`);
  }

  static limitMeasurement = (measurement: number | undefined) => {
    if (!measurement) {
      return undefined;
    }
    return measurement > WIDEN_MAX_IMAGE_MEASUREMENT ? WIDEN_MAX_IMAGE_MEASUREMENT : measurement;
  };
}
